import {
  Account,
  AggregationRequest,
  PaginationRequest,
  PayoutSource,
  PayoutWithProgram,
} from '@monorepo/types';
import { shouldDisablePolling } from '@hub/features/account-dashboard';
import {
  TRADES_POLLING_INTERVAL,
  TRADES_STALE_TIME,
} from '@hub/features/account-dashboard/hooks/constants';
import {
  QUERY_CONFIGS,
  QueryConfigKeys,
} from '@hub/utils/react-query/query-config';
import {
  fetchAirwallexAuth,
  fetchPaginatePayouts,
  fetchPayoutEligibility,
  fetchPayouts,
  fetchPayoutTotalsAndCounts,
} from '@hub/utils/react-query/query-functions';
import { QUERY_KEYS } from '@hub/utils/react-query/query-keys';
import { ExtractFnReturnType } from '@hub/utils/react-query/react-query';
import { useQuery } from '@tanstack/react-query';

const CONFIG_KEY = QueryConfigKeys.PAYOUTS;

export const usePayoutEligibility = (
  type: PayoutSource,
  sourceId: string,
  account?: Account,
) => {
  const isPollingDisabled = shouldDisablePolling(account);

  return useQuery<ExtractFnReturnType<typeof fetchPayoutEligibility>>({
    queryKey: QUERY_KEYS[CONFIG_KEY].ELIGIBILITY(type, sourceId),
    queryFn: () => fetchPayoutEligibility(type, sourceId),
    refetchInterval: isPollingDisabled ? false : TRADES_POLLING_INTERVAL, // Disable polling if account is disabled/paused
    refetchIntervalInBackground: false, // Don't poll when tab is not active
    staleTime: TRADES_STALE_TIME, // Data becomes stale after 25 seconds
  });
};

// TODO: Remove this query and use usePaginatePayouts instead
export const usePayouts = (
  type: PayoutSource,
  sourceId: number,
  paginateRequest: PaginationRequest,
) => {
  return useQuery<ExtractFnReturnType<typeof fetchPayouts>>({
    queryKey: QUERY_KEYS[CONFIG_KEY].LIST({
      ...paginateRequest,
      type,
      sourceId,
    }),
    queryFn: () => fetchPayouts(type, sourceId, paginateRequest),
    staleTime: QUERY_CONFIGS[CONFIG_KEY].staleTime,
  });
};

export const usePaginatePayouts = (
  paginateRequest: PaginationRequest<PayoutWithProgram>,
) => {
  return useQuery<ExtractFnReturnType<typeof fetchPaginatePayouts>>({
    queryKey: QUERY_KEYS[CONFIG_KEY].PAGINATE(paginateRequest),
    queryFn: () => fetchPaginatePayouts(paginateRequest),
    staleTime: QUERY_CONFIGS[CONFIG_KEY].staleTime,
  });
};

export const usePayoutTotalsAndCounts = (
  aggregationRequest: AggregationRequest,
) => {
  return useQuery<ExtractFnReturnType<typeof fetchPayoutTotalsAndCounts>>({
    queryKey: QUERY_KEYS[CONFIG_KEY].TOTALS_AND_COUNTS(aggregationRequest),
    queryFn: () => fetchPayoutTotalsAndCounts(aggregationRequest),
    staleTime: QUERY_CONFIGS[CONFIG_KEY].staleTime,
  });
};

export const useAirwallexAuth = () => {
  return useQuery<ExtractFnReturnType<typeof fetchAirwallexAuth>>({
    queryKey: QUERY_KEYS[CONFIG_KEY].AIRWALLEX_AUTH,
    queryFn: fetchAirwallexAuth,
    staleTime: QUERY_CONFIGS[CONFIG_KEY].staleTime,
  });
};
